import { type ApiContainerType, type ApiService } from '@Model';
import {
    FieldWithError,
    type ILinkInputProperties,
    useTranslate,
    useWizardErrors,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { type ContainerPosition } from '../../containerOrder';
import { ContainerPositionForm } from './ContainerPositionForm';
import { blockingDetailsError, contentError, type DetailsStepErrorKey } from './detailsErrors';

interface PlacementStepBodyProps {
    /** The container this order asks for, undefined while no type has been named. */
    position: ContainerPosition | undefined;
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    contentField: ILinkInputProperties<ApiService>;
    /** The waste code of the chosen content, empty while none is known. */
    avvCode: string;
    onCount: (count: number) => void;
    onLocationHint: (locationHint: string) => void;
}

/**
 * Step 3 of a placement: which container, how many, and what goes in.
 *
 * An order carries exactly one container, so the form *is* the step — it is filled in, not added
 * to, and every answer is saved as it is given. Placing containers needs no stock at all: a site
 * nobody has delivered to yet is exactly where a placement starts.
 */
export function PlacementStepBody({
    position,
    containerTypeField,
    contentField,
    avvCode,
    onCount,
    onLocationHint,
}: PlacementStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<DetailsStepErrorKey>();

    return (
        <>
            <WizardStepTitle
                title={i18n('Was für ein Container?')}
                subtitle={i18n('Sag uns Typ und Inhalt — wir bringen ihn dir.')}
            />

            {/* Sagt, warum „Weiter" nicht weitergeht — sonst sieht der Knopf kaputt aus. */}
            <FieldWithError error={blockingDetailsError(errors, position)}>
                <ContainerPositionForm
                    origin="placement"
                    position={position}
                    containerTypeField={containerTypeField}
                    contentField={contentField}
                    avvCode={avvCode}
                    contentError={contentError(errors, position)}
                    onCount={onCount}
                    location={{ label: i18n('Wo soll der Container stehen?'), onChange: onLocationHint }}
                />
            </FieldWithError>
        </>
    );
}
